import { MarkerClusterer } from "@googlemaps/markerclusterer";

(function () {
  const MAP_ID = "5995eb9877e35105df7be4ef";
  const MAP_CENTER = { lat: 52.377834, lng: 4.549954 };
  const MAP_ZOOM = 14;
  const ARCHIVE_SELECTOR = ".object-archive";
  const TOGGLE_SELECTOR = ".object-archive__map-toggle";
  const CANVAS_SELECTOR = ".object-archive__map__canvas";
  const GEOCODE_CACHE_PREFIX = "ewm-geocode:";

  let map = null;
  let mapInitialized = false;
  let initializing = false;
  let markerBounds = null;

  document.addEventListener("DOMContentLoaded", function () {
    const archive = document.querySelector(ARCHIVE_SELECTOR);
    const toggle = document.querySelector(TOGGLE_SELECTOR);

    if (!archive || !toggle) {
      return;
    }

    toggle.addEventListener("click", function () {
      setMapOpen(archive, toggle, !archive.classList.contains("is-map-open"));
    });
  });

  function setMapOpen(archive, toggle, isOpen) {
    archive.classList.toggle("is-map-open", isOpen);
    toggle.classList.toggle("is-active", isOpen);
    toggle.setAttribute("aria-expanded", isOpen ? "true" : "false");

    const label = toggle.querySelector(".object-archive__map-toggle-label");
    if (label) {
      label.textContent = isOpen ? "Lijst" : "Kaart";
    }

    if (!isOpen) {
      return;
    }

    if (!mapInitialized && !initializing) {
      initializeGoogleMaps();
      return;
    }

    refreshMapLayout();
  }

  function initializeGoogleMaps() {
    initializing = true;

    if (typeof google === "undefined" || typeof google.maps === "undefined") {
      let attempts = 0;
      const maxAttempts = 50;

      const checkGoogleMaps = setInterval(function () {
        attempts++;
        if (
          typeof google !== "undefined" &&
          typeof google.maps !== "undefined"
        ) {
          clearInterval(checkGoogleMaps);
          setupMap();
        } else if (attempts >= maxAttempts) {
          clearInterval(checkGoogleMaps);
          initializing = false;
          showApiError();
        }
      }, 100);

      return;
    }

    setupMap();
  }

  async function setupMap() {
    const mapContainer = document.querySelector(CANVAS_SELECTOR);
    if (!mapContainer) {
      initializing = false;
      return;
    }

    mapContainer.classList.add("loading");
    mapContainer.replaceChildren();

    try {
      const { Map } = await google.maps.importLibrary("maps");
      const { AdvancedMarkerElement } =
        await google.maps.importLibrary("marker");

      const mapOptions = {
        mapId: MAP_ID,
        zoom: MAP_ZOOM,
        center: MAP_CENTER,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
        zoomControlOptions: {
          position: google.maps.ControlPosition.RIGHT_BOTTOM,
        },
        disableDefaultUI: true,
        gestureHandling: "cooperative",
        maxZoom: 18,
        minZoom: 7,
      };

      if (
        typeof google.maps.RenderingType !== "undefined" &&
        google.maps.RenderingType.VECTOR
      ) {
        mapOptions.renderingType = google.maps.RenderingType.VECTOR;
      }

      map = new Map(mapContainer, mapOptions);

      const locations = await resolveLocations(getMarkerData());
      const markers = createMarkers(locations, AdvancedMarkerElement);

      if (markers.length) {
        try {
          new MarkerClusterer({
            markers,
            map,
            renderer: {
              render({ count, position }) {
                return new AdvancedMarkerElement({
                  position,
                  content: createClusterContent(count),
                  zIndex: 1000 + count,
                });
              },
            },
          });
        } catch (clusterError) {
          markers.forEach(function (marker) {
            marker.map = map;
          });
        }
      }

      mapContainer.classList.remove("loading");
      mapInitialized = true;
      initializing = false;
      refreshMapLayout();
    } catch (error) {
      initializing = false;
      showApiError();
    }
  }

  function getMarkerData() {
    if (
      typeof themeArchiveMap !== "undefined" &&
      Array.isArray(themeArchiveMap.markers)
    ) {
      return themeArchiveMap.markers;
    }

    return [];
  }

  function hasCoordinates(location) {
    const lat = Number(location.lat);
    const lng = Number(location.lng);
    return (
      Number.isFinite(lat) && Number.isFinite(lng) && lat !== 0 && lng !== 0
    );
  }

  async function resolveLocations(locations) {
    const resolved = [];
    const pending = [];

    locations.forEach(function (location) {
      if (hasCoordinates(location)) {
        resolved.push(location);
        return;
      }

      if (!location.address) {
        return;
      }

      const cached = readGeocodeCache(location.address);
      if (cached) {
        persistCoordinates(location.id, cached);
        resolved.push(
          Object.assign({}, location, { lat: cached.lat, lng: cached.lng }),
        );
        return;
      }

      pending.push(location);
    });

    const results = await Promise.all(
      pending.map(async function (location) {
        const coords = await geocodeAddress(location.address);
        if (!coords) {
          return null;
        }

        writeGeocodeCache(location.address, coords);
        persistCoordinates(location.id, coords);

        return Object.assign({}, location, {
          lat: coords.lat,
          lng: coords.lng,
        });
      }),
    );

    results.filter(Boolean).forEach(function (location) {
      resolved.push(location);
    });

    return resolved;
  }

  async function geocodeAddress(address) {
    const pdokCoords = await geocodeWithPdok(address);
    if (pdokCoords) {
      return pdokCoords;
    }

    return geocodeWithGoogle(address);
  }

  async function geocodeWithPdok(address) {
    try {
      const url = new URL(
        "https://api.pdok.nl/bzk/locatieserver/search/v3_1/free",
      );
      url.searchParams.set("fq", "type:adres");
      url.searchParams.set("rows", "1");
      url.searchParams.set("q", address);

      const response = await fetch(url.toString());
      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      const point =
        data && data.response && data.response.docs && data.response.docs[0]
          ? data.response.docs[0].centroide_ll
          : "";
      const match = /POINT\(([0-9.\-]+) ([0-9.\-]+)\)/.exec(point || "");

      if (!match) {
        return null;
      }

      return {
        lng: parseFloat(match[1]),
        lat: parseFloat(match[2]),
      };
    } catch (error) {
      return null;
    }
  }

  function geocodeWithGoogle(address, attempt) {
    const retry = attempt || 0;

    if (
      typeof google === "undefined" ||
      !google.maps ||
      !google.maps.Geocoder
    ) {
      return Promise.resolve(null);
    }

    const geocoder = new google.maps.Geocoder();

    return new Promise(function (resolve) {
      geocoder.geocode(
        { address: address, region: "NL" },
        function (results, status) {
          if (status === "OK" && results && results[0]) {
            resolve({
              lat: results[0].geometry.location.lat(),
              lng: results[0].geometry.location.lng(),
            });
            return;
          }

          if (status === "OVER_QUERY_LIMIT" && retry < 4) {
            setTimeout(
              function () {
                geocodeWithGoogle(address, retry + 1).then(resolve);
              },
              350 * (retry + 1),
            );
            return;
          }

          resolve(null);
        },
      );
    });
  }

  function readGeocodeCache(address) {
    try {
      const raw = window.localStorage.getItem(GEOCODE_CACHE_PREFIX + address);
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw);
      if (!hasCoordinates(parsed)) {
        return null;
      }

      return { lat: Number(parsed.lat), lng: Number(parsed.lng) };
    } catch (error) {
      return null;
    }
  }

  function writeGeocodeCache(address, coords) {
    try {
      window.localStorage.setItem(
        GEOCODE_CACHE_PREFIX + address,
        JSON.stringify(coords),
      );
    } catch (error) {
      // Ignore quota errors.
    }
  }

  function persistCoordinates(postId, coords) {
    if (
      !postId ||
      typeof themeArchiveMap === "undefined" ||
      !themeArchiveMap.ajaxUrl
    ) {
      return;
    }

    const body = new URLSearchParams({
      action: "theme_save_object_map_coords",
      nonce: themeArchiveMap.nonce || "",
      id: String(postId),
      lat: String(coords.lat),
      lng: String(coords.lng),
    });

    window.fetch(themeArchiveMap.ajaxUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
      },
      body: body.toString(),
    });
  }

  function createMarkers(locations, AdvancedMarkerElement) {
    const markers = [];
    markerBounds = new google.maps.LatLngBounds();

    locations.forEach(function (location) {
      const lat = Number(location.lat);
      const lng = Number(location.lng);

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return;
      }

      const position = { lat, lng };
      const marker = new AdvancedMarkerElement({
        position,
        title: location.title || "",
        content: createPriceMarker(location),
        gmpClickable: Boolean(location.url),
      });

      if (location.url) {
        marker.addEventListener("gmp-click", function () {
          window.location.href = location.url;
        });
      }

      markerBounds.extend(position);
      markers.push(marker);
    });

    return markers;
  }

  function formatMapPrice(price) {
    const amount = Number(price);

    if (!Number.isFinite(amount) || amount <= 0) {
      return "";
    }

    if (amount >= 1000000) {
      const millions = Math.round((amount / 1000000) * 10) / 10;
      const label = Number.isInteger(millions)
        ? String(millions)
        : String(millions).replace(".", ",");
      return "€ " + label + "M";
    }

    return "€ " + Math.round(amount / 1000) + "k";
  }

  function createPriceMarker(location) {
    const markerElement = document.createElement(location.url ? "a" : "div");
    markerElement.className = "object-archive__map__marker";
    markerElement.textContent = formatMapPrice(location.price) || "€";

    if (location.url) {
      markerElement.href = location.url;
    }

    if (location.title) {
      markerElement.setAttribute("aria-label", location.title);
    }

    return markerElement;
  }

  function createClusterContent(count) {
    const clusterElement = document.createElement("div");
    clusterElement.className = "object-archive__map__cluster";
    clusterElement.textContent = String(count);
    return clusterElement;
  }

  function refreshMapLayout() {
    if (!map) {
      return;
    }

    window.requestAnimationFrame(function () {
      setTimeout(function () {
        if (markerBounds && !markerBounds.isEmpty()) {
          map.fitBounds(markerBounds, 80);

          google.maps.event.addListenerOnce(map, "idle", function () {
            if (map.getZoom() > MAP_ZOOM) {
              map.setZoom(MAP_ZOOM);
            }
          });
          return;
        }

        map.setCenter(MAP_CENTER);
        map.setZoom(MAP_ZOOM);
      }, 80);
    });
  }

  function showApiError() {
    const mapContainer = document.querySelector(CANVAS_SELECTOR);
    if (!mapContainer) {
      return;
    }

    mapContainer.classList.add("error");
    mapContainer.innerHTML =
      "<div><p>Google Maps kon niet worden geladen</p></div>";
  }
})();
